/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2025-2026 (original work) Open Assessment Technologies SA;
 */

import { get, writable } from 'svelte/store';
import { DATE_FORMAT_KEYS, resolveDateFormatEntries } from '@oat-sa-private/ui-core';
import { getTenantConfiguration } from '@/services/tenantConfigurationService';
import { applyDateFormatSettings, DEFAULT_DATE_FORMAT_LOCALE } from '@/core/utils/dateFormatApply.js';
import { publishTenantConfigurationDiagnostics } from '@/core/utils/tenantConfigurationDiagnostics.js';

export const currentTenantStore = writable(null);

const DATE_FORMAT_ENTRY_KEYS = new Set(Object.values(DATE_FORMAT_KEYS));

function pickDateFormatEntries(source = {}) {
    return Object.fromEntries(Object.entries(source).filter(([key]) => DATE_FORMAT_ENTRY_KEYS.has(key)));
}

/**
 * @param {object} configuration
 * @returns {object}
 */
function resolveDateFormats(configuration = {}) {
    const dateFormatsConfig = configuration?.dateFormats;
    if (!dateFormatsConfig || typeof dateFormatsConfig !== 'object') {
        return {};
    }

    const resolvedEnabled = Boolean(dateFormatsConfig?.enabled ?? false);
    let dateFormats = {};

    if (dateFormatsConfig?.dateFormats && typeof dateFormatsConfig?.dateFormats === 'object') {
        dateFormats = dateFormatsConfig.dateFormats;
    } else {
        dateFormats = pickDateFormatEntries(dateFormatsConfig);
    }

    return {
        enabled: resolvedEnabled,
        dateFormats
    };
}

function isLocalisedDatesConfigEnabled(configuration = {}) {
    return Boolean(configuration?.localisedDatesConfigEnabled);
}

function getAppliedDateFormats(configuration = {}) {
    const dateFormats = resolveDateFormats(configuration);
    const isFeatureEnabled = isLocalisedDatesConfigEnabled(configuration);
    const localizedEnabled = Boolean(isFeatureEnabled && dateFormats.enabled);
    const hasTenantEntries = Object.keys(dateFormats.dateFormats ?? {}).length > 0;

    return {
        enabled: localizedEnabled,
        dateFormats:
            localizedEnabled || hasTenantEntries
                ? resolveDateFormatEntries({
                      enabled: localizedEnabled,
                      dateFormats: dateFormats.dateFormats ?? {}
                  })
                : {}
    };
}

/**
 * Factory to create configuration store and forward the get, set, subscribe methods to svelte store,
 * adding convenience methods as well
 * @returns {object}
 */
function createConfigurationStore() {
    const configurationStore = writable(null);

    return {
        get() {
            return get(configurationStore);
        },
        set: configurationStore.set,
        subscribe: configurationStore.subscribe,
        clear: () => configurationStore.set(null),
        setCurrentTenant(tenant) {
            currentTenantStore.set(tenant);
        },
        getCurrentTenant() {
            return get(currentTenantStore);
        },
        async updateConfigurationData(applicationLocale = DEFAULT_DATE_FORMAT_LOCALE) {
            const { configuration = {} } = await getTenantConfiguration();
            const dateFormats = getAppliedDateFormats(configuration);

            await applyDateFormatSettings({
                enabled: dateFormats.enabled,
                dateFormats: dateFormats.dateFormats ?? {},
                locale: applicationLocale || DEFAULT_DATE_FORMAT_LOCALE
            });

            configurationStore.set({ ...configuration });
            publishTenantConfigurationDiagnostics(configuration, dateFormats);
        },
        getDateFormats() {
            const configuration = get(configurationStore);
            return getAppliedDateFormats(configuration) || {};
        },
        isLocalizedDateFormatsEnabled() {
            const configuration = get(configurationStore);
            return Boolean(getAppliedDateFormats(configuration)?.enabled);
        },
        getUserpilotToken() {
            const configuration = get(configurationStore);
            return configuration?.userpilot?.token;
        }
    };
}

export const configurationStore = createConfigurationStore();
